/* eslint-disable @typescript-eslint/lines-between-class-members */
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  Renderer2,
  signal,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, EMPTY, finalize, Subject, takeUntil } from 'rxjs';

import { AuthService } from 'src/app/core/services/auth';
import { SharedModule } from 'src/app/shared/shared.module';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, SharedModule, CommonModule, RouterLink],
})
export class ForgotPasswordComponent implements AfterViewInit, OnDestroy {
  forgotPasswordFormGroup: UntypedFormGroup = new UntypedFormGroup({
    username: new UntypedFormControl('', [Validators.required]),
  });
  displayForm = signal(true);
  usernameValid = signal(false);
  errorMessage = signal('');
  isLoading = signal(false);

  private destroy$ = new Subject<void>();
  private readonly authService = inject(AuthService);
  private readonly renderer = inject(Renderer2);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);

  ngAfterViewInit(): void {
    this.renderer.selectRootElement('#username').focus();
    this.forgotPasswordFormGroup.controls.username.setValue(
      this.route.snapshot.queryParamMap.get('username') || '',
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submitForgotPasswordForm(): void {
    const username = this.forgotPasswordFormGroup.controls.username
      .value as string;
    this.errorMessage.set('');

    if (!username.length) {
      this.errorMessage.set('Please enter a username.');
      return;
    }
    const regex = /(\s*$)|(^\s*)/g;
    const cleanUsername = username.replace(regex, '');
    this.isLoading.set(true);

    this.authService
      .forgotPassword(cleanUsername)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading.set(false);
        }),
        catchError((err: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.errorMessage.set(
            err?.status === 0
              ? 'Network error. Please check your connection and try again.'
              : 'We couldn’t process your request right now. Please try again later.',
          );
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.displayForm.set(false);
        this.isLoading.set(false);
      });
  }
}
